import React, { useState, useEffect } from 'react';
import {
  ComposableBlock,
  ComposableBlockType,
  InterventionRecipe,
  SomaticBlockConfig,
  CognitiveBlockConfig,
  FrictionBlockConfig,
  SupportBlockConfig,
  DEFAULT_DOMAIN_RECIPES,
  createDefaultRecipeForDomain,
  createDefaultSomaticBlock,
  createDefaultCognitiveBlock,
  createDefaultFrictionBlock,
  createDefaultSupportBlock,
  reorderBlocks,
  addBlockToRecipe,
  removeBlockFromRecipe,
  updateBlockInRecipe,
  validateRecipe,
} from '../sync/recipe-types';
import { BehavioralDomainId } from '../sync/target-types';
import { BEHAVIORAL_DOMAINS } from '../sync/preset-catalog';

export interface IndividualRecipeBuilderProps {
  initialDomain?: BehavioralDomainId;
  initialRecipe?: InterventionRecipe;
  onSaveRecipe?: (recipe: InterventionRecipe) => void;
  onPreviewRecipe?: (recipe: InterventionRecipe) => void;
}

const AVAILABLE_DISTORTIONS = [
  'Catastrophizing',
  'Emotional Reasoning',
  'Urgency Illusion',
  'All-or-Nothing',
  'Dopamine Loop',
  'Gambler Fallacy',
  'Overconfidence',
  'Somatic Tension',
  'Mind Reading',
];

const AVAILABLE_VALUES = [
  'Restorative Sleep',
  'Mental Clarity',
  'Financial Freedom',
  'Family Presence',
  'Physical Vitality',
  'Emotional Sobriety',
  'Focused Deep Work',
  'Authentic Connection',
];

export const IndividualRecipeBuilder: React.FC<IndividualRecipeBuilderProps> = ({
  initialDomain = 'financial_speculation',
  initialRecipe,
  onSaveRecipe,
  onPreviewRecipe,
}) => {
  const [selectedDomain, setSelectedDomain] = useState<BehavioralDomainId>(initialDomain);
  const [recipe, setRecipe] = useState<InterventionRecipe>(() => {
    if (initialRecipe) return initialRecipe;
    return createDefaultRecipeForDomain(initialDomain, 'self_subscriber', 'Personal Mindful Shield');
  });
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(() => recipe.blocks[0]?.id || null);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Keep selected block valid if blocks change
  useEffect(() => {
    if (recipe.blocks.length > 0) {
      if (!selectedBlockId || !recipe.blocks.some((b) => b.id === selectedBlockId)) {
        setSelectedBlockId(recipe.blocks[0].id);
      }
    } else {
      setSelectedBlockId(null);
    }
  }, [recipe.blocks, selectedBlockId]);

  // Handler: Change Domain
  const handleDomainChange = (domain: BehavioralDomainId) => {
    setSelectedDomain(domain);
    const domainMeta = BEHAVIORAL_DOMAINS.find((d) => d.id === domain);
    const baseRecipe = createDefaultRecipeForDomain(
      domain,
      'self_subscriber',
      `Personal ${domainMeta?.shortName || 'Shield'} Recipe`
    );
    setRecipe(baseRecipe);
    setSelectedBlockId(baseRecipe.blocks[0]?.id || null);
    setSaveStatus(null);
    setValidationErrors([]);
  };

  // Handler: Move Block Up/Down
  const handleMoveBlock = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= recipe.blocks.length) return;
    const reordered = reorderBlocks(recipe.blocks, index, targetIndex);
    const updated: InterventionRecipe = {
      ...recipe,
      blocks: reordered,
      updatedAt: new Date().toISOString(),
    };
    setRecipe(updated);
    onPreviewRecipe?.(updated);
  };

  // Handler: Add Block
  const handleAddBlock = (type: ComposableBlockType) => {
    let newBlock: ComposableBlock;
    if (type === 'somatic') {
      newBlock = createDefaultSomaticBlock('box_4_4_4');
    } else if (type === 'cognitive') {
      newBlock = createDefaultCognitiveBlock('cbt_reframing');
    } else if (type === 'friction') {
      newBlock = createDefaultFrictionBlock('cooling_off_lock', 45);
    } else {
      newBlock = createDefaultSupportBlock('voice_video_note');
    }

    const updated = addBlockToRecipe(recipe, newBlock);
    setRecipe(updated);
    setSelectedBlockId(newBlock.id);
    onPreviewRecipe?.(updated);
  };

  // Handler: Remove Block
  const handleRemoveBlock = (blockId: string) => {
    if (recipe.blocks.length <= 1) {
      alert('A break recipe must contain at least one intervention step.');
      return;
    }
    const updated = removeBlockFromRecipe(recipe, blockId);
    setRecipe(updated);
    onPreviewRecipe?.(updated);
  };

  // Handler: Save Personal Recipe
  const handleSaveRecipe = () => {
    const validation = validateRecipe(recipe);
    if (!validation.valid) {
      setValidationErrors(validation.errors);
      return;
    }
    setValidationErrors([]);
    const savedRecipe: InterventionRecipe = {
      ...recipe,
      updatedAt: new Date().toISOString(),
    };
    setRecipe(savedRecipe);
    onSaveRecipe?.(savedRecipe);
    onPreviewRecipe?.(savedRecipe);
    setSaveStatus(`✓ Recipe "${savedRecipe.name}" saved locally with Zero-Knowledge encryption.`);
    setTimeout(() => setSaveStatus(null), 4000);
  };

  // Handler: Reset to Canonical Preset
  const handleResetToPreset = () => {
    const domainMeta = BEHAVIORAL_DOMAINS.find((d) => d.id === selectedDomain);
    const resetRecipe = createDefaultRecipeForDomain(
      selectedDomain,
      'self_subscriber',
      `Personal ${domainMeta?.shortName || 'Mindful'} Shield`
    );
    setRecipe(resetRecipe);
    setSelectedBlockId(resetRecipe.blocks[0]?.id || null);
    onPreviewRecipe?.(resetRecipe);
    setSaveStatus('Restored canonical therapeutic preset.');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const selectedBlock = recipe.blocks.find((b) => b.id === selectedBlockId);
  const currentDomainMeta = BEHAVIORAL_DOMAINS.find((d) => d.id === selectedDomain);

  const getBlockTypeIcon = (type: ComposableBlockType) => {
    switch (type) {
      case 'somatic':
        return '🫁';
      case 'cognitive':
        return '🧠';
      case 'friction':
        return '⏳';
      case 'support':
        return '🤝';
    }
  };

  const getBlockTypeBadgeColor = (type: ComposableBlockType) => {
    switch (type) {
      case 'somatic':
        return { bg: '#E0F2FE', text: '#0369A1', border: '#BAE6FD' };
      case 'cognitive':
        return { bg: '#EDE9FE', text: '#6D28D9', border: '#DDD6FE' };
      case 'friction':
        return { bg: '#FEF3C7', text: '#B45309', border: '#FDE68A' };
      case 'support':
        return { bg: '#D1FAE5', text: '#047857', border: '#A7F3D0' };
    }
  };

  return (
    <div
      className="individual-recipe-builder-root"
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        border: '1px solid #E2E8F0',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
      }}
    >
      {/* Header & Domain Selector */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          borderBottom: '1px solid #E2E8F0',
          paddingBottom: '20px',
          marginBottom: '20px',
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '24px' }}>{currentDomainMeta?.icon || '🧩'}</span>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1E293B' }}>
              Personal Break Recipe Builder
            </h3>
            <span
              style={{
                backgroundColor: currentDomainMeta?.bgSoftColor || '#F1F5F9',
                color: currentDomainMeta?.accentColor || '#475569',
                border: `1px solid ${currentDomainMeta?.accentColor || '#CBD5E1'}40`,
                borderRadius: '20px',
                padding: '2px 10px',
                fontSize: '11px',
                fontWeight: 700,
              }}
            >
              {currentDomainMeta?.displayName || selectedDomain}
            </span>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
            Customize your multi-step neuro-grounding sequence. Changes are saved locally with Zero-Knowledge guarantees.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="builder-domain-select" style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>
            Domain:
          </label>
          <select
            id="builder-domain-select"
            value={selectedDomain}
            onChange={(e) => handleDomainChange(e.target.value as BehavioralDomainId)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FAF8F5',
              fontSize: '13px',
              fontWeight: 600,
              color: '#1E293B',
              cursor: 'pointer',
            }}
          >
            {BEHAVIORAL_DOMAINS.map((domain) => (
              <option key={domain.id} value={domain.id}>
                {domain.icon} {domain.displayName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Recipe Name & Top Actions */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          backgroundColor: '#FAF8F5',
          padding: '12px 16px',
          borderRadius: '12px',
          border: '1px solid #E2E8F0',
        }}
      >
        <div style={{ flex: '1 1 280px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="recipe-title-input" style={{ fontSize: '12px', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
            Recipe Title:
          </label>
          <input
            id="recipe-title-input"
            type="text"
            value={recipe.name}
            onChange={(e) => {
              const updated = { ...recipe, name: e.target.value };
              setRecipe(updated);
              onPreviewRecipe?.(updated);
            }}
            placeholder="e.g. My 2-Minute Grounding Circuit"
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              fontSize: '13px',
              fontWeight: 600,
              color: '#1E293B',
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={handleResetToPreset}
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#64748B',
              cursor: 'pointer',
            }}
          >
            ↺ Reset Preset
          </button>
          <button
            type="button"
            onClick={handleSaveRecipe}
            style={{
              backgroundColor: '#5E8276',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 700,
              color: '#FFFFFF',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(94,130,118,0.3)',
            }}
          >
            💾 Save Recipe
          </button>
        </div>
      </div>

      {saveStatus && (
        <div
          role="status"
          style={{
            backgroundColor: '#DCFCE7',
            color: '#166534',
            border: '1px solid #86EFAC',
            borderRadius: '8px',
            padding: '10px 16px',
            marginBottom: '16px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {saveStatus}
        </div>
      )}

      {validationErrors.length > 0 && (
        <div
          role="alert"
          style={{
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            padding: '10px 16px',
            marginBottom: '16px',
            fontSize: '12px',
          }}
        >
          <strong>Validation Errors:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
            {validationErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Main Builder Grid: Canvas on Left, Block Inspector on Right */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '20px',
        }}
      >
        {/* Left Column: Composable Block Sequence Canvas */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#1E293B' }}>
              Execution Sequence ({recipe.blocks.length} Steps)
            </h4>
            <span style={{ fontSize: '11px', color: '#64748B' }}>Click step to customize parameters</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
            {recipe.blocks.map((block, index) => {
              const isSelected = block.id === selectedBlockId;
              const badge = getBlockTypeBadgeColor(block.type);
              return (
                <div
                  key={block.id}
                  onClick={() => setSelectedBlockId(block.id)}
                  style={{
                    backgroundColor: isSelected ? '#F8FAFC' : '#FFFFFF',
                    border: isSelected ? '2px solid #5E8276' : '1px solid #E2E8F0',
                    borderRadius: '12px',
                    padding: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    boxShadow: isSelected ? '0 4px 12px rgba(94,130,118,0.12)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          backgroundColor: '#1E293B',
                          color: '#FFFFFF',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 800,
                        }}
                      >
                        {index + 1}
                      </span>
                      <span
                        style={{
                          backgroundColor: badge.bg,
                          color: badge.text,
                          border: `1px solid ${badge.border}`,
                          borderRadius: '6px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                        }}
                      >
                        {getBlockTypeIcon(block.type)} {block.type}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        aria-label={`Move ${block.title} up`}
                        disabled={index === 0}
                        onClick={() => handleMoveBlock(index, 'up')}
                        style={{
                          border: '1px solid #CBD5E1',
                          backgroundColor: index === 0 ? '#F1F5F9' : '#FFFFFF',
                          color: index === 0 ? '#94A3B8' : '#334155',
                          borderRadius: '6px',
                          padding: '3px 8px',
                          fontSize: '11px',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${block.title} down`}
                        disabled={index === recipe.blocks.length - 1}
                        onClick={() => handleMoveBlock(index, 'down')}
                        style={{
                          border: '1px solid #CBD5E1',
                          backgroundColor: index === recipe.blocks.length - 1 ? '#F1F5F9' : '#FFFFFF',
                          color: index === recipe.blocks.length - 1 ? '#94A3B8' : '#334155',
                          borderRadius: '6px',
                          padding: '3px 8px',
                          fontSize: '11px',
                          cursor: index === recipe.blocks.length - 1 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        ▼
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${block.title}`}
                        disabled={recipe.blocks.length <= 1}
                        onClick={() => handleRemoveBlock(block.id)}
                        style={{
                          border: '1px solid #FECACA',
                          backgroundColor: '#FEF2F2',
                          color: '#DC2626',
                          borderRadius: '6px',
                          padding: '3px 8px',
                          fontSize: '11px',
                          cursor: recipe.blocks.length <= 1 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1E293B' }}>{block.title}</div>
                    <div style={{ fontSize: '12px', color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {block.instructions}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add Block Palette */}
          <div
            style={{
              backgroundColor: '#FAF8F5',
              border: '1px dashed #CBD5E1',
              borderRadius: '12px',
              padding: '14px',
            }}
          >
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '8px' }}>
              Add Intervention Block:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <button
                type="button"
                onClick={() => handleAddBlock('somatic')}
                style={{
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid #BAE6FD',
                  backgroundColor: '#F0F9FF',
                  color: '#0369A1',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                ➕ 🫁 Somatic Breath
              </button>
              <button
                type="button"
                onClick={() => handleAddBlock('cognitive')}
                style={{
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid #DDD6FE',
                  backgroundColor: '#F5F3FF',
                  color: '#6D28D9',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                ➕ 🧠 Cognitive CBT
              </button>
              <button
                type="button"
                onClick={() => handleAddBlock('friction')}
                style={{
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid #FDE68A',
                  backgroundColor: '#FFFBEB',
                  color: '#B45309',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                ➕ ⏳ Friction Lock
              </button>
              <button
                type="button"
                onClick={() => handleAddBlock('support')}
                style={{
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: '1px solid #A7F3D0',
                  backgroundColor: '#ECFDF5',
                  color: '#047857',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                ➕ 🤝 Support / 988
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Selected Block Parameter Inspector */}
        <div
          style={{
            backgroundColor: '#FAF8F5',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            padding: '16px',
          }}
        >
          {selectedBlock ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#1E293B' }}>
                  Configure Step: {selectedBlock.title}
                </h4>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#64748B',
                  }}
                >
                  Type: {selectedBlock.type}
                </span>
              </div>

              {/* Title & Instructions Input */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                    Step Display Title
                  </label>
                  <input
                    type="text"
                    value={selectedBlock.title}
                    onChange={(e) => {
                      const updated = updateBlockInRecipe(recipe, selectedBlock.id, { title: e.target.value });
                      setRecipe(updated);
                      onPreviewRecipe?.(updated);
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #CBD5E1',
                      fontSize: '12px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                    User Guidance Instructions
                  </label>
                  <textarea
                    rows={2}
                    value={selectedBlock.instructions}
                    onChange={(e) => {
                      const updated = updateBlockInRecipe(recipe, selectedBlock.id, { instructions: e.target.value });
                      setRecipe(updated);
                      onPreviewRecipe?.(updated);
                    }}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #CBD5E1',
                      fontSize: '12px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              {/* Somatic Config */}
              {selectedBlock.type === 'somatic' && selectedBlock.somaticConfig && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#FFFFFF', padding: '14px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Breathing Pattern Variant
                    </label>
                    <select
                      value={selectedBlock.somaticConfig.variant}
                      onChange={(e) => {
                        const variant = e.target.value as SomaticBlockConfig['variant'];
                        let inhale = 4;
                        let hold = 4;
                        let exhale = 4;
                        let cycles = 4;
                        if (variant === 'relaxing_4_7_8') {
                          inhale = 4;
                          hold = 7;
                          exhale = 8;
                          cycles = 3;
                        } else if (variant === 'diaphragmatic_mandala') {
                          inhale = 5;
                          hold = 0;
                          exhale = 5;
                          cycles = 3;
                        }
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          somaticConfig: {
                            ...selectedBlock.somaticConfig!,
                            variant,
                            inhaleSeconds: inhale,
                            holdSeconds: hold,
                            exhaleSeconds: exhale,
                            cycles,
                          },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                    >
                      <option value="box_4_4_4">Box Breathing (4-4-4-0s)</option>
                      <option value="relaxing_4_7_8">Relaxing Parasympathetic (4-7-8s)</option>
                      <option value="diaphragmatic_mandala">Diaphragmatic Mandala (5-0-5s)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Total Breath Cycles: {selectedBlock.somaticConfig.cycles}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="8"
                      value={selectedBlock.somaticConfig.cycles}
                      onChange={(e) => {
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          somaticConfig: { ...selectedBlock.somaticConfig!, cycles: Number(e.target.value) },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{ width: '100%', accentColor: '#0369A1' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748B' }}>Inhale:</span>
                      <input
                        type="number"
                        min="2"
                        max="10"
                        value={selectedBlock.somaticConfig.inhaleSeconds}
                        onChange={(e) => {
                          const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                            somaticConfig: { ...selectedBlock.somaticConfig!, inhaleSeconds: Number(e.target.value) },
                          });
                          setRecipe(updated);
                          onPreviewRecipe?.(updated);
                        }}
                        style={{ width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #CBD5E1', fontSize: '11px' }}
                      />
                    </div>
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748B' }}>Hold:</span>
                      <input
                        type="number"
                        min="0"
                        max="12"
                        value={selectedBlock.somaticConfig.holdSeconds}
                        onChange={(e) => {
                          const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                            somaticConfig: { ...selectedBlock.somaticConfig!, holdSeconds: Number(e.target.value) },
                          });
                          setRecipe(updated);
                          onPreviewRecipe?.(updated);
                        }}
                        style={{ width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #CBD5E1', fontSize: '11px' }}
                      />
                    </div>
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748B' }}>Exhale:</span>
                      <input
                        type="number"
                        min="2"
                        max="12"
                        value={selectedBlock.somaticConfig.exhaleSeconds}
                        onChange={(e) => {
                          const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                            somaticConfig: { ...selectedBlock.somaticConfig!, exhaleSeconds: Number(e.target.value) },
                          });
                          setRecipe(updated);
                          onPreviewRecipe?.(updated);
                        }}
                        style={{ width: '100%', padding: '4px', borderRadius: '4px', border: '1px solid #CBD5E1', fontSize: '11px' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Cognitive Config */}
              {selectedBlock.type === 'cognitive' && selectedBlock.cognitiveConfig && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#FFFFFF', padding: '14px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      CBT Protocol Variant
                    </label>
                    <select
                      value={selectedBlock.cognitiveConfig.variant}
                      onChange={(e) => {
                        const variant = e.target.value as CognitiveBlockConfig['variant'];
                        let prompt = 'Notice the urge. Is this aligned with your goals?';
                        if (variant === 'craving_vs_need') prompt = 'Rate your craving (1-10). What genuine bodily need is asking for care?';
                        if (variant === 'loss_probability') prompt = 'Consider the consequence: will your future self thank you for stepping away now?';
                        if (variant === 'urge_surfing') prompt = 'Visualize this urge cresting like a wave. It peaks and subsides within 90 seconds.';
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          cognitiveConfig: { ...selectedBlock.cognitiveConfig!, variant, promptQuestion: prompt },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                    >
                      <option value="cbt_reframing">CBT Thought Reframing</option>
                      <option value="craving_vs_need">Craving vs. Need Inquiry</option>
                      <option value="loss_probability">Loss Probability & Consequences</option>
                      <option value="urge_surfing">Mindful Urge Surfing</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Dialectical Prompt Question
                    </label>
                    <textarea
                      rows={3}
                      value={selectedBlock.cognitiveConfig.promptQuestion}
                      onChange={(e) => {
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          cognitiveConfig: { ...selectedBlock.cognitiveConfig!, promptQuestion: e.target.value },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #CBD5E1',
                        fontSize: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '6px' }}>
                      Distortion / Awareness Tags
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {AVAILABLE_DISTORTIONS.map((tag) => {
                        const active = selectedBlock.cognitiveConfig?.distortionTags?.includes(tag);
                        return (
                          <button
                            type="button"
                            key={tag}
                            onClick={() => {
                              const current = selectedBlock.cognitiveConfig?.distortionTags || [];
                              const next = active ? current.filter((t) => t !== tag) : [...current, tag];
                              const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                                cognitiveConfig: { ...selectedBlock.cognitiveConfig!, distortionTags: next },
                              });
                              setRecipe(updated);
                              onPreviewRecipe?.(updated);
                            }}
                            style={{
                              border: active ? '1px solid #7C3AED' : '1px solid #CBD5E1',
                              backgroundColor: active ? '#EDE9FE' : '#FAF8F5',
                              color: active ? '#6D28D9' : '#475569',
                              padding: '3px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              cursor: 'pointer',
                            }}
                          >
                            {tag}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Friction Config */}
              {selectedBlock.type === 'friction' && selectedBlock.frictionConfig && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#FFFFFF', padding: '14px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Friction Mechanism Variant
                    </label>
                    <select
                      value={selectedBlock.frictionConfig.variant}
                      onChange={(e) => {
                        const variant = e.target.value as FrictionBlockConfig['variant'];
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          frictionConfig: { ...selectedBlock.frictionConfig!, variant },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                    >
                      <option value="cooling_off_lock">Cooling-Off Time-Lock</option>
                      <option value="values_contract">Values-Alignment Contract</option>
                      <option value="reflection_prompt">Intentional Reflection Pause</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Intentional Delay: {selectedBlock.frictionConfig.lockDurationSeconds}s
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[15, 30, 60, 120, 300].map((sec) => (
                        <button
                          type="button"
                          key={sec}
                          onClick={() => {
                            const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                              frictionConfig: { ...selectedBlock.frictionConfig!, lockDurationSeconds: sec },
                            });
                            setRecipe(updated);
                            onPreviewRecipe?.(updated);
                          }}
                          style={{
                            flex: 1,
                            padding: '6px 4px',
                            borderRadius: '6px',
                            border: selectedBlock.frictionConfig?.lockDurationSeconds === sec ? '1.5px solid #D97706' : '1px solid #CBD5E1',
                            backgroundColor: selectedBlock.frictionConfig?.lockDurationSeconds === sec ? '#FEF3C7' : '#FAF8F5',
                            color: '#1E293B',
                            fontWeight: 700,
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                        >
                          {sec}s
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Personal Pledge Commitment
                    </label>
                    <input
                      type="text"
                      value={selectedBlock.frictionConfig.pledgeText || ''}
                      onChange={(e) => {
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          frictionConfig: { ...selectedBlock.frictionConfig!, pledgeText: e.target.value },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #CBD5E1',
                        fontSize: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '6px' }}>
                      Anchor Core Values
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {AVAILABLE_VALUES.map((val) => {
                        const active = selectedBlock.frictionConfig?.coreValues?.includes(val);
                        return (
                          <button
                            type="button"
                            key={val}
                            onClick={() => {
                              const current = selectedBlock.frictionConfig?.coreValues || [];
                              const next = active ? current.filter((v) => v !== val) : [...current, val];
                              const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                                frictionConfig: { ...selectedBlock.frictionConfig!, coreValues: next },
                              });
                              setRecipe(updated);
                              onPreviewRecipe?.(updated);
                            }}
                            style={{
                              border: active ? '1px solid #D97706' : '1px solid #CBD5E1',
                              backgroundColor: active ? '#FEF3C7' : '#FAF8F5',
                              color: active ? '#B45309' : '#475569',
                              padding: '3px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              cursor: 'pointer',
                            }}
                          >
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Support Config */}
              {selectedBlock.type === 'support' && selectedBlock.supportConfig && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', backgroundColor: '#FFFFFF', padding: '14px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Support Modality
                    </label>
                    <select
                      value={selectedBlock.supportConfig.variant}
                      onChange={(e) => {
                        const variant = e.target.value as SupportBlockConfig['variant'];
                        let title = 'Therapist Voice Note';
                        let sponsorNumber = '';
                        let transcript = 'Take a deep breath and ground yourself.';
                        if (variant === 'crisis_988') {
                          title = '988 Suicide & Crisis Lifeline';
                          sponsorNumber = '988';
                          transcript = 'Free, confidential, 24/7 support is available by phone or text at 988.';
                        } else if (variant === 'sponsor_dialer') {
                          title = 'Care Circle Sponsor Dialer';
                          sponsorNumber = '+1-800-555-0199';
                          transcript = 'Reach out to your trusted recovery advocate.';
                        }
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          supportConfig: {
                            ...selectedBlock.supportConfig!,
                            variant,
                            mediaTitle: title,
                            sponsorPhoneNumber: sponsorNumber,
                            transcript,
                          },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                    >
                      <option value="voice_video_note">Supportive Audio / Voice Memo</option>
                      <option value="crisis_988">988 Suicide & Crisis Lifeline</option>
                      <option value="sponsor_dialer">Care Circle Sponsor Dialer</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Media Title / Note Heading
                    </label>
                    <input
                      type="text"
                      value={selectedBlock.supportConfig.mediaTitle || ''}
                      onChange={(e) => {
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          supportConfig: { ...selectedBlock.supportConfig!, mediaTitle: e.target.value },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #CBD5E1',
                        fontSize: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {selectedBlock.supportConfig.variant !== 'voice_video_note' && (
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                        Contact / Hotline Number
                      </label>
                      <input
                        type="text"
                        value={selectedBlock.supportConfig.sponsorPhoneNumber || ''}
                        onChange={(e) => {
                          const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                            supportConfig: { ...selectedBlock.supportConfig!, sponsorPhoneNumber: e.target.value },
                          });
                          setRecipe(updated);
                          onPreviewRecipe?.(updated);
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #CBD5E1',
                          fontSize: '12px',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                      Grounding Transcript / Reassurance Copy
                    </label>
                    <textarea
                      rows={3}
                      value={selectedBlock.supportConfig.transcript || ''}
                      onChange={(e) => {
                        const updated = updateBlockInRecipe(recipe, selectedBlock.id, {
                          supportConfig: { ...selectedBlock.supportConfig!, transcript: e.target.value },
                        });
                        setRecipe(updated);
                        onPreviewRecipe?.(updated);
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #CBD5E1',
                        fontSize: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 10px', color: '#64748B', fontSize: '13px' }}>
              Select a block from the sequence canvas to inspect and edit its parameters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndividualRecipeBuilder;
